// constants.ts — every tuned number in the Rheora AI module, in one place.
//
// CLAUDE.md forbids magic numbers in components. These are module-local rather
// than in `@fluxora/constants` because every one of them is a property of THIS
// chatbot host's measured behaviour, not of the platform.
//
// The latency figures below are measurements taken against
// `dev-global-rheora-crm-chatbot.rheora.ai` on 2026-09-22, not estimates:
//
//   guard replies (chit_chat / out_of_scope / ambiguous)   1.6s – 2.2s
//   data questions (text-to-SQL round trip)                6.6s – 7.7s
//
// That split is the single most important fact about this feature's UX, because
// at request time we cannot tell which of the two we are about to get.

/**
 * Hard limits the server enforces on `POST /api/v1/chat`.
 *
 * Mirrored here so the composer can refuse locally instead of spending a round
 * trip to collect a 422. `QUESTION_MAX_LENGTH` is the server's `maxLength`, not
 * a design choice — keep them equal.
 */
export const QUESTION_MIN_LENGTH = 1;
export const QUESTION_MAX_LENGTH = 2000;

/**
 * Where the character counter appears: 80% of the limit.
 *
 * A permanent `0/2000` under a box most questions fill forty characters of is
 * noise that trains the eye to skip the area. At 80% it is information.
 */
export const QUESTION_COUNTER_VISIBLE_FROM = 1600;

/**
 * Turns of prior conversation sent as `history`.
 *
 * The server documents that it uses only the last 10, so sending more is pure
 * payload. Trimming client-side also bounds a body the server does not bound:
 * it caps `question`, but says nothing about `history`.
 */
export const HISTORY_MAX_TURNS = 10;

/**
 * Per-turn cap on `history[].content`, matching the question cap.
 *
 * An answer is server-generated prose and has no documented ceiling, so without
 * this a long answer could make a later request's body unbounded.
 */
export const HISTORY_CONTENT_MAX_LENGTH = 2000;

/**
 * Request timeout.
 *
 * The shared factory's default is 15_000, which is under 2x headroom over the
 * worst measured response (7.7s) — and the server performs its own internal
 * retries (`retry_count` is part of the contract), so a query rebuild plus a
 * cold model can plausibly cross 15s. 25s is ~3x worst-measured and still
 * inside human patience *given the elapsed counter is on screen*.
 *
 * Deliberately NOT 60s: the ingress cuts at ~60s anyway (nginx default, no
 * `proxy-read-timeout` annotation), and past ~25s the useful move is to re-ask,
 * not to keep waiting.
 */
export const CHAT_TIMEOUT_MS = 25_000;

/**
 * Progress thresholds, in ms since the request started. See
 * `services/progress.service.ts` for what each one renders.
 *
 * `ELAPSED_VISIBLE_FROM` is 3s and not 0s on purpose: a stopwatch on a 1.8s
 * greeting makes a fast, correct answer feel supervised. After 3s it is the most
 * reassuring thing on screen, because it converts "is this stuck?" into "it has
 * been four seconds".
 *
 * `STILL_WORKING_FROM` is 7s because by 7s we KNOW it is a data question — the
 * guard paths have all returned by ~2.2s — so a message naming data questions is
 * true rather than a guess.
 */
export const ELAPSED_VISIBLE_FROM_MS = 3_000;
export const STILL_WORKING_FROM_MS = 7_000;
export const LONGER_THAN_USUAL_FROM_MS = 15_000;

/** How often the pending bubble re-renders its elapsed counter. */
export const ELAPSED_TICK_MS = 1_000;

/** Composer autosize bounds, in rows. */
export const COMPOSER_MIN_ROWS = 1;
export const COMPOSER_MAX_ROWS = 6;

/**
 * Viewport width at or above which the panel is a FLOATING CARD, and below
 * which it becomes a near-full-screen modal sheet.
 *
 * 640px, not 1280. The old 1280 threshold belonged to the docked column, where
 * the question was "is there room to work *beside* a 420px column?" — a question
 * about the page's remaining width. A floating card asks a narrower one: "is
 * there room for a 420px card plus its margins at all?" 420 + 2×20 = 460, so
 * anything from ~480px up can physically hold it.
 *
 * The real bound is the operator, not the arithmetic. Below 640px the page
 * behind the card is too narrow for a CRM table to be useful anyway, so the
 * "click the row behind it" affordance the non-modal card exists to protect has
 * nothing left to protect — and at that point covering the window and trapping
 * focus is the honest behaviour rather than a compromise. 640px is also
 * Tailwind's `sm` breakpoint, so it is the boundary the rest of this app already
 * treats as "phone".
 *
 * See `AiAssistantDock` for what changes at the threshold.
 */
export const FLOATING_MIN_VIEWPORT_PX = 640;
